import React, { useState } from 'react';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  useTheme,
  useMediaQuery,
  Badge,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Logout,
  Notifications,
  Fullscreen,
  FullscreenExit,
} from '@mui/icons-material';
import { useAuth } from '@/hooks/useAuth';
import { useSettings } from '@/hooks/useSettings';
import { useFullscreen } from '@/hooks/useFullscreen';
import { useFullscreenContext } from '@/contexts/FullscreenContext';
import { useSelector, useDispatch } from 'react-redux';
import ZoomControls from './ZoomControls';
import {
  selectSidebarOpen,
  selectUnreadNotificationsCount,
  toggleSidebar,
  setSidebarOpen,
} from '@/store/slices/uiSlice';
import { selectStoreAndOutlet } from '@/store/slices/storeSlice';
import Sidebar from './Sidebar';
import NotificationPanel from './NotificationPanel';
import ToastNotification from './ToastNotification';
import ContextDebugger from './ContextDebugger';
import StoreOutletSelector from '../store/StoreOutletSelector';
import LanguageSwitcher from './LanguageSwitcher';
import { getLogoUrl } from '@/utils/imageUrl';
import { useFavicon } from '@/hooks/useFavicon';

interface MainLayoutProps {
  children: React.ReactNode;
}

const DRAWER_WIDTH = '17.5rem';

const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const dispatch = useDispatch();
  const { user, logout } = useAuth();
  const { settings } = useSettings();
  const { isFullscreen } = useFullscreen();
  const { handleFullscreenToggle } = useFullscreenContext();
  const sidebarOpen = useSelector(selectSidebarOpen);
  const unreadCount = useSelector(selectUnreadNotificationsCount);
  const { storeName } = useSelector(selectStoreAndOutlet);
  useFavicon();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [notificationOpen, setNotificationOpen] = useState(false);

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    handleProfileMenuClose();
    await logout();
  };

  const handleDrawerToggle = () => {
    dispatch(toggleSidebar());
  };

  const handleDrawerClose = () => {
    if (isMobile) {
      dispatch(setSidebarOpen(false));
    }
  };

  const drawer = (
    <Sidebar onItemClick={handleDrawerClose} />
  );

  return (
    <Box sx={{ display: 'flex' }}>
      {/* App Bar */}
      <AppBar
        position="fixed"
        sx={{
          width: !isMobile && sidebarOpen ? `calc(100% - ${DRAWER_WIDTH})` : '100%',
          ml: !isMobile && sidebarOpen ? `${DRAWER_WIDTH}` : 0,
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
          backgroundColor: 'background.paper',
          color: 'text.primary',
          boxShadow: 'none',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Toolbar sx={{ px: { xs: 1, sm: 2 }, minHeight: { xs: 56, sm: 64 } }}>
          <IconButton
            color="inherit"
            aria-label="toggle drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: { xs: 0.5, sm: 1 } }}
          >
            <MenuIcon />
          </IconButton>

          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1, minWidth: 0 }}>
            {settings?.logoUrl ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 }, minWidth: 0 }}>
                <Box
                  component="img"
                  src={getLogoUrl(settings.logoUrl)}
                  alt={storeName || 'Store Logo'}
                  sx={{
                    height: { xs: 28, sm: 32 },
                    width: 'auto',
                    maxWidth: { xs: 36, sm: 48 },
                    objectFit: 'contain',
                    flexShrink: 0,
                  }}
                  onError={(e) => {
                    // Hide image if it fails to load
                    e.currentTarget.style.display = 'none';
                  }}
                />
                <Typography
                  variant="h6"
                  noWrap
                  component="div"
                  sx={{
                    fontSize: { xs: '0.9rem', sm: '1.25rem' },
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {storeName || 'Ankassa'}
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  component="img"
                  src="/assets/logo.svg"
                  alt="Ankassa"
                  sx={{
                    height: 32,
                    width: 'auto',
                    maxWidth: 48,
                    objectFit: 'contain',
                  }}
                />
                <Typography
                  variant="h6"
                  noWrap
                  component="div"
                >
                  {storeName || 'Ankassa'}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Store/Outlet Selector */}
          {!isMobile && <StoreOutletSelector />}

          {/* Zoom Controls - Hide on mobile */}
          {!isMobile && <ZoomControls />}

          {/* Fullscreen Toggle */}
          {/* Fullscreen Toggle */}
          {!isMobile && (
            <IconButton
              color="inherit"
              onClick={handleFullscreenToggle}
              title={isFullscreen ? 'Exit Fullscreen (Password Required)' : 'Enter Fullscreen'}
              size={isMobile ? 'small' : 'medium'}
            >
              {isFullscreen ? <FullscreenExit /> : <Fullscreen />}
            </IconButton>
          )}

          {/* Language Switcher */}
          <LanguageSwitcher />

          {/* Notifications */}
          <IconButton
            color="inherit"
            onClick={() => setNotificationOpen(true)}
            size={isMobile ? 'small' : 'medium'}
          >
            <Badge badgeContent={unreadCount} color="error">
              <Notifications />
            </Badge>
          </IconButton>

          {/* User Profile - Hide on mobile to save space */}
          {!isMobile && (
            <IconButton
              size="large"
              edge="end"
              aria-label="account of current user"
              aria-controls="profile-menu"
              aria-haspopup="true"
              onClick={handleProfileMenuOpen}
              color="inherit"
              sx={{ ml: 1 }}
            >
              <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
                {user?.name?.charAt(0).toUpperCase() || 'U'}
              </Avatar>
            </IconButton>
          )}
        </Toolbar>
      </AppBar>

      {/* Profile Menu */}
      <Menu
        id="profile-menu"
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        keepMounted
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
        PaperProps={{
          elevation: 3,
          sx: {
            mt: 1,
            minWidth: 200,
            '& .MuiMenuItem-root': {
              borderRadius: 1,
              mx: 1,
              my: 0.5,
            },
          },
        }}
      >
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography variant="subtitle1" fontWeight="medium">
            {user?.name || 'User'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {user?.username}
          </Typography>
        </Box>
        <Divider />
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <Logout fontSize="small" />
          </ListItemIcon>
          <ListItemText>Logout</ListItemText>
        </MenuItem>
      </Menu>

      {/* Navigation Drawer */}
      <Box
        component="nav"
        sx={{ width: !isMobile && sidebarOpen ? DRAWER_WIDTH : 0, flexShrink: !isMobile ? 0 : undefined }}
      >
        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={sidebarOpen && isMobile}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile
          }}
          sx={{
            display: isMobile ? 'block' : 'none',
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: DRAWER_WIDTH,
            },
          }}
        >
          {drawer}
        </Drawer>

        {/* Desktop drawer */}
        <Drawer
          variant="persistent"
          open={sidebarOpen && !isMobile}
          sx={{
            display: !isMobile ? 'block' : 'none',
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: DRAWER_WIDTH,
              transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
            },
          }}
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: !isMobile && sidebarOpen ? `calc(100% - ${DRAWER_WIDTH})` : '100%',
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        <Toolbar /> {/* Spacer for fixed AppBar */}
        <Box sx={{
          p: { xs: 1.5, sm: 2, md: 3 },
          position: 'relative',
          // Ensure the inner content doesn't exceed viewport height (subtract Toolbar + padding)
          minHeight: { xs: 'calc(100vh - 3.5rem - 1.5rem)', sm: 'calc(100vh - 4rem - 2rem)', md: 'calc(100vh - 4rem - 3rem)' }
        }}>
          {children}
        </Box>
      </Box>

      {/* Notification Panel */}
      <NotificationPanel
        open={notificationOpen}
        onClose={() => setNotificationOpen(false)}
      />

      {/* Toast Notifications */}
      <ToastNotification />

      {/* Session Debugger */}
      <ContextDebugger />
    </Box>
  );
};

export default MainLayout;
